import { getQuickReplies, createQuickReply, updateQuickReply, deleteQuickReply } from '../api/quick-replies.api';
import type { QuickReply, CreateQuickReplyDto, UpdateQuickReplyDto } from '../types/quick-replies';

export interface QuickReplyItem {
  id: string;
  trigger: string;
  response: string;
}

export const transformQuickReply = (catalog: QuickReply): QuickReplyItem => ({
  id: catalog.id,
  trigger: catalog.jsonData.trigger,
  response: catalog.jsonData.response
});

export const getQuickRepliesList = async (): Promise<QuickReplyItem[]> => {
  try {
    const catalogs = await getQuickReplies();
    return catalogs
      .filter(catalog => catalog.jsonData?.trigger && catalog.jsonData?.response)
      .map(transformQuickReply);
  } catch (error) {
    console.error('Error fetching quick replies:', error);
    return [];
  }
};

export const addQuickReply = async (trigger: string, response: string): Promise<QuickReplyItem> => {
  try {
    const createDto: CreateQuickReplyDto = {
      name: trigger,
      priority: 1,
      jsonData: { trigger, response }
    };

    const catalog = await createQuickReply(createDto);
    return transformQuickReply(catalog);
  } catch (error) {
    console.error('Error creating quick reply:', error);
    throw error;
  }
};

export const editQuickReply = async (id: string, trigger: string, response: string): Promise<QuickReplyItem> => {
  try {
    const updateDto: UpdateQuickReplyDto = {
      name: trigger,
      priority: 1,
      jsonData: { trigger, response }
    };

    const catalog = await updateQuickReply(id, updateDto);
    return transformQuickReply(catalog);
  } catch (error) {
    console.error('Error updating quick reply:', error);
    throw error;
  }
};

export const removeQuickReply = async (id: string): Promise<void> => {
  try {
    await deleteQuickReply(id);
  } catch (error) {
    console.error('Error deleting quick reply:', error);
    throw error;
  }
};

export const checkQuickReply = async (message: string): Promise<string | null> => {
  try {
    const quickReplies = await getQuickRepliesList();
    const trimmedMessage = message.trim();

    for (const quickReply of quickReplies) {
      if (trimmedMessage === quickReply.trigger) {
        return quickReply.response;
      }
    }

    return null;
  } catch (error) {
    console.error('Error checking quick reply:', error);
    return null;
  }
};

let cachedQuickReplies: QuickReplyItem[] | null = null;

export const checkQuickReplySync = (message: string): string | null => {
  if (!cachedQuickReplies) return null;

  const trimmedMessage = message.trim();

  for (const quickReply of cachedQuickReplies) {
    if (trimmedMessage === quickReply.trigger) {
      return quickReply.response;
    }
  }

  return null;
};

export const cacheQuickReplies = (replies: QuickReplyItem[]) => {
  cachedQuickReplies = replies;
};

export const clearQuickRepliesCache = () => {
  cachedQuickReplies = null;
};
